import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { Globe, Plus, Trash2, CalendarDays, Shield, Clock, Bell } from "lucide-react";
import { Calendar } from "../components/ui/calendar";
import { motion, AnimatePresence } from "framer-motion";
import ConfirmDialog from "../components/ConfirmDialog";
import PageIntro from "../components/PageIntro";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "../components/ui/sonner";

const countries = [
  { value: "UAE", label: "United Arab Emirates" },
  { value: "North Cyprus", label: "North Cyprus" },
  { value: "Germany", label: "Germany" },
  { value: "Other", label: "Other" },
];

// Residency Tracker — country → visa-type catalogue.
// Selecting a country in the form filters the Visa Type dropdown to just that
// country's options (+ "Other" for anything we don't list yet).
const residencyCountries = [
  { value: "UAE", label: "United Arab Emirates" },
  { value: "North Cyprus", label: "North Cyprus" },
  { value: "Schengen Area", label: "Schengen Area (EU)" },
  { value: "Panama", label: "Panama" },
  { value: "Paraguay", label: "Paraguay" },
  { value: "Portugal", label: "Portugal" },
  { value: "Spain", label: "Spain" },
  { value: "Malta", label: "Malta" },
  { value: "Greece", label: "Greece" },
  { value: "Turkey", label: "Turkey" },
  { value: "Thailand", label: "Thailand" },
  { value: "Singapore", label: "Singapore" },
  { value: "United Kingdom", label: "United Kingdom" },
  { value: "United States", label: "United States" },
  { value: "Other", label: "Other" },
];

// Country code → list of available visa types. "Other" is appended automatically.
const visaTypesByCountry = {
  "UAE": [
    "Dubai Golden Visa",
    "Dubai Investor Visa",
    "Dubai Employment Visa",
    "UAE Green Visa",
  ],
  "North Cyprus": [
    "North Cyprus Residency Permit",
    "North Cyprus Student Permit",
  ],
  "Schengen Area": [
    "Schengen Visa (Type C, short-stay)",
    "Schengen Long-Stay Visa (Type D)",
    "EU Blue Card",
  ],
  "Panama": [
    "Panama Friendly Nations Visa",
    "Panama Pensionado Visa",
  ],
  "Paraguay": [
    "Paraguay Permanent Residency",
  ],
  "Portugal": [
    "Portugal D7 Passive Income Visa",
    "Portugal Golden Visa",
  ],
  "Spain": [
    "Spain Non-Lucrative Visa",
    "Spain Golden Visa",
  ],
  "Malta": [
    "Malta Permanent Residency",
  ],
  "Greece": [
    "Greece Golden Visa",
  ],
  "Turkey": [
    "Turkey Citizenship by Investment",
  ],
  "Thailand": [
    "Thailand Elite Visa",
  ],
  "Singapore": [
    "Singapore Employment Pass",
  ],
  "United Kingdom": [
    "UK Innovator Founder Visa",
  ],
  "United States": [
    "US E-2 Investor Visa",
  ],
  "Other": [],
};

// Helper: return the visa list for a given country, always appended with "Other".
const visaTypesFor = (country) => [...(visaTypesByCountry[country] || []), "Other"];

// Uppercase-key / mono-value row, mirrors the landing-page Residency mock styling.
function ResidencyRow({ k, v }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-white/55 text-[11px] tracking-wider uppercase font-sans">{k}</span>
      <span className="text-white font-mono text-xs">{v}</span>
    </div>
  );
}

export default function MobilityTrackerPage() {
  const { user } = useAuth();
  const [residencies, setResidencies] = useState([]);
  const [presenceLogs, setPresenceLogs] = useState([]);
  const [presenceSummary, setPresenceSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddRes, setShowAddRes] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedRange, setSelectedRange] = useState(undefined);  // { from, to }
  const [logMode, setLogMode] = useState("single");  // "single" | "range"
  const [logCountry, setLogCountry] = useState("UAE");
  const [logCountryCustom, setLogCountryCustom] = useState("");
  // The actual country string sent to the API and used for dedupe checks
  const effectiveLogCountry = logCountry === "Other" ? logCountryCustom.trim() : logCountry;
  const [rangeSubmitting, setRangeSubmitting] = useState(false);
  const [confirmState, setConfirmState] = useState(null);  // { title, description, onConfirm, confirmLabel }

  const [resForm, setResForm] = useState({
    country: "UAE", visa_type: "Dubai Golden Visa",
    holder_name: "",
    start_date: "", expiry_date: "", notes: ""
  });

  const fetchData = useCallback(async () => {
    try {
      const [resRes, logRes, sumRes] = await Promise.all([
        api.get("/residencies"),
        api.get("/presence"),
        api.get("/presence/summary"),
      ]);
      setResidencies(resRes.data);
      setPresenceLogs(logRes.data);
      setPresenceSummary(sumRes.data.by_country || {});
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAddResidency = async (e) => {
    e.preventDefault();
    const payload = {
      country: resForm.country === "Other" ? (resForm.country_custom || "").trim() : resForm.country,
      visa_type: resForm.visa_type === "Other" ? (resForm.visa_type_custom || "").trim() : resForm.visa_type,
      start_date: resForm.start_date,
      expiry_date: resForm.expiry_date,
      notes: resForm.notes,
      holder_name: (resForm.holder_name || "").trim(),
    };
    if (!payload.country) {
      toast.warning("Please enter a country name.");
      return;
    }
    if (!payload.visa_type) {
      toast.warning("Please enter a visa type.");
      return;
    }
    try {
      await api.post("/residencies", payload);
      fetchData();
      setShowAddRes(false);
      setResForm({ country: "UAE", visa_type: "Dubai Golden Visa", holder_name: "", start_date: "", expiry_date: "", notes: "", country_custom: "", visa_type_custom: "" });
      toast.success("Residency added");
    } catch (err) {
      toast.error("Could not add residency", { description: err.response?.data?.detail });
    }
  };

  const handleLogPresence = async () => {
    if (!selectedDate) return;
    if (!effectiveLogCountry) {
      toast.warning("Please type a country name in the 'Other' field.");
      return;
    }
    const dateStr = selectedDate.toISOString().split("T")[0];
    try {
      await api.post("/presence", { country: effectiveLogCountry, date: dateStr });
      fetchData();
      toast.success("Day logged", { description: `${effectiveLogCountry} — ${dateStr}` });
    } catch (err) {
      toast.error("Failed to log presence", { description: err.response?.data?.detail });
    }
  };

  const toIsoDate = (d) => {
    // Normalize to local YYYY-MM-DD to avoid UTC off-by-one
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const handleLogRange = async () => {
    if (!selectedRange?.from || !selectedRange?.to) return;
    if (!effectiveLogCountry) {
      toast.warning("Please type a country name in the 'Other' field.");
      return;
    }
    setRangeSubmitting(true);
    try {
      const { data } = await api.post("/presence/range", {
        country: effectiveLogCountry,
        start_date: toIsoDate(selectedRange.from),
        end_date: toIsoDate(selectedRange.to),
      });
      await fetchData();
      setSelectedRange(undefined);
      toast.success(
        `Logged ${data.inserted} day${data.inserted === 1 ? "" : "s"}`,
        data.skipped_duplicates
          ? { description: `${data.skipped_duplicates} already existed and were skipped.` }
          : { description: `${effectiveLogCountry}` }
      );
    } catch (err) {
      toast.error("Failed to log range", { description: err.response?.data?.detail });
    } finally {
      setRangeSubmitting(false);
    }
  };

  const handleDeletePresence = async (id) => {
    try {
      await api.delete(`/presence/${id}`);
      fetchData();
      toast.success("Day removed");
    } catch (err) {
      toast.error("Failed to delete", { description: err.response?.data?.detail });
    }
  };

  const handleCalendarDayClick = (date) => {
    if (!date) {
      setSelectedDate(null);
      return;
    }
    const iso = toIsoDate(date);
    const matchCountry = effectiveLogCountry;
    const existing = matchCountry ? presenceLogs.find(l => l.date === iso && l.country === matchCountry) : null;
    if (existing) {
      setConfirmState({
        title: `Delete presence log?`,
        description: (
          <>
            This will remove the <span className="text-white font-medium">{matchCountry}</span> presence log for <span className="text-white font-medium">{iso}</span>. This action cannot be undone.
          </>
        ),
        confirmLabel: "Delete Day",
        onConfirm: () => {
          handleDeletePresence(existing.id);
          setSelectedDate(null);
          setConfirmState(null);
        },
      });
      return;
    }
    setSelectedDate(date);
  };

  const handleDeleteRange = async () => {
    if (!selectedRange?.from || !selectedRange?.to) return;
    const start = toIsoDate(selectedRange.from);
    const end = toIsoDate(selectedRange.to);
    const overlapCount = effectiveLogCountry
      ? presenceLogs.filter(
          l => l.country === effectiveLogCountry && l.date >= start && l.date <= end
        ).length
      : 0;
    if (!overlapCount) {
      toast.info("No logged days in this range for the selected country.");
      return;
    }
    setConfirmState({
      title: `Delete ${overlapCount} logged day${overlapCount === 1 ? "" : "s"}?`,
      description: (
        <>
          This will remove all <span className="text-white font-medium">{effectiveLogCountry}</span> presence logs from <span className="text-white font-medium">{start}</span> to <span className="text-white font-medium">{end}</span>. This action cannot be undone.
        </>
      ),
      confirmLabel: `Delete ${overlapCount} Day${overlapCount === 1 ? "" : "s"}`,
      onConfirm: async () => {
        setConfirmState(null);
        setRangeSubmitting(true);
        try {
          const { data } = await api.delete(`/presence/range`, {
            params: { country: effectiveLogCountry, start_date: start, end_date: end },
          });
          await fetchData();
          setSelectedRange(undefined);
          toast.success(`Deleted ${data.deleted} day${data.deleted === 1 ? "" : "s"}`);
        } catch (err) {
          toast.error("Failed to delete range", { description: err.response?.data?.detail });
        } finally {
          setRangeSubmitting(false);
        }
      },
    });
  };

  const handleDeleteResidency = async (id) => {
    try {
      await api.delete(`/residencies/${id}`);
      fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  // Highlight dates in the calendar where presence is logged
  const loggedDates = presenceLogs.map(l => new Date(l.date + "T00:00:00"));

  const getDaysUntilExpiry = (expiryDate) => {
    const now = new Date();
    const expiry = new Date(expiryDate);
    return Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="mobility-tracker-page">
      <PageIntro
        pageKey="mobility"
        title="Residency & Mobility Tracker"
        subtitle="Track visas, log days-in-country, and never overstay a renewal deadline."
        bullets={[
          { icon: Shield, label: "Add residencies", text: "Golden Visa (UAE), Permanent Residency (North Cyprus), German Aufenthaltstitel — with expiry dates." },
          { icon: CalendarDays, label: "Log presence days", text: "Click days on the calendar to log time in-country. Critical for tax residency and visa renewal eligibility." },
          { icon: Clock, label: "Auto reminders", text: "We email you 60 / 30 / 14 / 3 days before any visa expires — quietly, in the background." },
        ]}
      />
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-[11px] tracking-[0.15em] uppercase text-white/60 font-sans mb-1">Global Mobility</p>
          <h1 className="font-serif text-3xl sm:text-4xl text-white tracking-tight">Residency Tracker</h1>
          <p className="text-white/60 text-sm mt-1">Track visas, log presence days, and stay compliant</p>
        </div>
        <button
          onClick={() => setShowAddRes(true)}
          data-testid="add-residency-btn"
          className="bg-[#D4AF37] text-black px-5 py-2.5 text-sm font-medium hover:bg-[#B5952F] transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Add Residency
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Residencies */}
        <div className="lg:col-span-2 space-y-4">
          <p className="text-[11px] tracking-[0.15em] uppercase text-white/60">Active Residencies</p>
          {residencies.length > 0 ? residencies.map(res => {
            const daysLeft = getDaysUntilExpiry(res.expiry_date);
            const urgency = daysLeft <= 0 ? "expired" : daysLeft <= 90 ? "urgent" : daysLeft <= 180 ? "warning" : "ok";

            // Progress bar — elapsed time between start and expiry
            const progressPct = (() => {
              if (!res.start_date || !res.expiry_date) return 0;
              const start = new Date(res.start_date).getTime();
              const end = new Date(res.expiry_date).getTime();
              const now = Date.now();
              if (end <= start) return 0;
              return Math.min(100, Math.max(0, ((now - start) / (end - start)) * 100));
            })();

            // Reminder scheduling — backend fires at 60/30/14/3 days before expiry
            const reminderActive = daysLeft > 0 && daysLeft <= 90;

            const holderName = (res.holder_name && res.holder_name.trim())
              || user?.name
              || user?.email?.split("@")[0]
              || "—";
            const fmtDate = (d) => {
              if (!d) return "—";
              try {
                return new Date(d).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
              } catch { return d; }
            };

            return (
              <motion.div
                key={res.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-[#0A0A0A] border border-white/[0.12] p-5 sm:p-6 relative overflow-hidden hover:border-[#D4AF37]/15 transition-colors"
                data-testid={`residency-${res.id}`}
              >
                {/* Subtle gold halo, top-right */}
                <div
                  aria-hidden
                  className="absolute -top-12 -right-12 w-40 h-40 opacity-[0.06] pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(closest-side, rgba(212,175,55,0.8), transparent)",
                  }}
                />

                {/* Header — eyebrow + serif title + shield icon */}
                <div className="flex items-start justify-between mb-5 relative">
                  <div>
                    <p className="text-[9px] tracking-[0.2em] uppercase text-white/55 font-sans mb-2">
                      Residency · {res.country}
                    </p>
                    <h3 className="font-serif text-xl text-white">{res.visa_type}</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <Shield className={`w-5 h-5 ${
                      urgency === "expired" ? "text-red-400" :
                      urgency === "urgent" ? "text-amber-400" :
                      "text-[#D4AF37]"
                    }`} strokeWidth={1.5} />
                    <button
                      onClick={() => handleDeleteResidency(res.id)}
                      data-testid={`delete-residency-${res.id}`}
                      className="text-white/55 hover:text-red-400 transition-colors p-1"
                      aria-label="Delete residency"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Rows — Holder / Expires / Status */}
                <div className="space-y-3 mb-5 relative">
                  <ResidencyRow k="Holder" v={holderName} />
                  <ResidencyRow k="Expires" v={fmtDate(res.expiry_date)} />
                  <ResidencyRow
                    k="Status"
                    v={
                      <span className={
                        urgency === "expired" ? "text-red-400" :
                        urgency === "urgent" ? "text-amber-400" :
                        urgency === "warning" ? "text-yellow-400" :
                        "text-emerald-400"
                      }>
                        {urgency === "expired" ? "Expired" : `Active · ${daysLeft} days`}
                      </span>
                    }
                  />
                </div>

                {/* Countdown strip — elapsed/total */}
                <div className="relative h-1 bg-white/[0.06] mb-4">
                  <div
                    className={`absolute inset-y-0 left-0 ${
                      urgency === "expired" ? "bg-red-400" :
                      urgency === "urgent" ? "bg-amber-400" :
                      "bg-[#D4AF37]"
                    }`}
                    style={{ width: `${progressPct}%` }}
                    data-testid={`residency-progress-${res.id}`}
                  />
                </div>

                {/* Renewal reminder strip — amber band, only when active */}
                {reminderActive && (
                  <div
                    className="flex items-start gap-2 px-3 py-2.5 bg-[#D4AF37]/[0.06] border border-[#D4AF37]/20"
                    data-testid={`residency-reminder-${res.id}`}
                  >
                    <Bell className="w-3.5 h-3.5 text-[#D4AF37] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    <p className="text-[11px] text-white/90 leading-relaxed">
                      Renewal reminder scheduled · 60 days before expiry. {res.country}-specific checklist + estimated fees auto-attached.
                    </p>
                  </div>
                )}

                {res.notes && (
                  <p className="text-white/65 text-xs mt-3 leading-relaxed relative">{res.notes}</p>
                )}
              </motion.div>
            );
          }) : (
            <div className="bg-[#0A0A0A] border border-white/[0.10] p-10 text-center">
              <Globe className="w-10 h-10 text-white/10 mx-auto mb-3" strokeWidth={1} />
              <p className="text-white/65 text-sm">No residencies tracked yet</p>
            </div>
          )}

          {/* Presence Summary */}
          <div className="bg-[#0A0A0A] border border-white/[0.10] p-5" data-testid="presence-summary">
            <p className="text-[11px] tracking-[0.15em] uppercase text-white/60 mb-4">Presence Days ({new Date().getFullYear()})</p>
            {Object.keys(presenceSummary).length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {Object.entries(presenceSummary).map(([country, days]) => (
                  <div key={country} className="bg-white/[0.02] border border-white/[0.08] p-4">
                    <p className="text-white/60 text-xs mb-1">{country}</p>
                    <p className="font-mono text-2xl text-white">{days}</p>
                    <p className="text-white/55 text-[10px]">days logged</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-white/55 text-sm">No days logged yet this year</p>
            )}
          </div>
        </div>

        {/* Calendar & Presence Logger */}
        <div className="lg:col-span-1 space-y-4">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#0A0A0A] border border-white/[0.10] p-5"
            data-testid="presence-logger"
          >
            <p className="text-[11px] tracking-[0.15em] uppercase text-white/60 mb-4">Log Presence</p>
            {logMode === "single" && (
              <p className="text-[10px] text-white/65 mb-3 -mt-2">Tip: click a gold-highlighted day again to delete it.</p>
            )}

            {/* Mode toggle */}
            <div className="flex gap-2 mb-4" data-testid="log-mode-toggle">
              <button
                onClick={() => setLogMode("single")}
                data-testid="mode-single"
                className={`flex-1 px-3 py-1.5 text-[11px] tracking-[0.12em] uppercase border transition-all ${
                  logMode === "single"
                    ? "bg-[#D4AF37]/10 border-[#D4AF37]/30 text-[#D4AF37]"
                    : "border-white/10 text-white/60 hover:border-white/20"
                }`}
              >
                Single Day
              </button>
              <button
                onClick={() => setLogMode("range")}
                data-testid="mode-range"
                className={`flex-1 px-3 py-1.5 text-[11px] tracking-[0.12em] uppercase border transition-all ${
                  logMode === "range"
                    ? "bg-[#D4AF37]/10 border-[#D4AF37]/30 text-[#D4AF37]"
                    : "border-white/10 text-white/60 hover:border-white/20"
                }`}
              >
                Date Range
              </button>
            </div>

            <div className="flex justify-center mb-4 [&_.rdp]:text-white [&_.rdp-day_selected]:bg-[#D4AF37] [&_.rdp-day_selected]:text-black [&_.rdp-day_today]:border [&_.rdp-day_today]:border-[#D4AF37]/50 [&_.rdp-head_cell]:text-white/65 [&_.rdp-caption]:text-white [&_.rdp-nav_button]:text-white/60 [&_.rdp-nav_button:hover]:text-white [&_.rdp-cell]:text-white/85 [&_.rdp-day:hover]:bg-white/10">
              {logMode === "single" ? (
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleCalendarDayClick}
                  modifiers={{ logged: loggedDates }}
                  modifiersStyles={{ logged: { backgroundColor: "rgba(212,175,55,0.2)", color: "#D4AF37" } }}
                  className="rounded-none"
                />
              ) : (
                <Calendar
                  mode="range"
                  selected={selectedRange}
                  onSelect={setSelectedRange}
                  numberOfMonths={1}
                  modifiers={{ logged: loggedDates }}
                  modifiersStyles={{ logged: { backgroundColor: "rgba(212,175,55,0.2)", color: "#D4AF37" } }}
                  className="rounded-none"
                />
              )}
            </div>

            {logMode === "range" && selectedRange?.from && selectedRange?.to && (
              <p className="text-[11px] text-white/60 text-center mb-3" data-testid="range-preview">
                {Math.round((selectedRange.to - selectedRange.from) / 86400000) + 1} days selected
              </p>
            )}

            <div className="space-y-3">
              <div>
                <label className="text-[10px] tracking-[0.15em] uppercase text-white/65 mb-1.5 block">Country</label>
                <select
                  data-testid="presence-country-select"
                  value={logCountry}
                  onChange={e => setLogCountry(e.target.value)}
                  className="w-full bg-[#050505] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#D4AF37] outline-none"
                >
                  {countries.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                {logCountry === "Other" && (
                  <input
                    type="text"
                    data-testid="presence-country-custom"
                    value={logCountryCustom}
                    onChange={e => setLogCountryCustom(e.target.value)}
                    placeholder="e.g. Portugal, Singapore…"
                    className="mt-2 w-full bg-[#050505] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#D4AF37] outline-none"
                  />
                )}
              </div>

              {logMode === "single" ? (
                <button
                  onClick={handleLogPresence}
                  disabled={!selectedDate}
                  data-testid="log-presence-btn"
                  className="w-full bg-[#D4AF37] text-black py-2.5 text-sm font-medium hover:bg-[#B5952F] transition-colors disabled:opacity-30"
                >
                  <CalendarDays className="w-4 h-4 inline mr-2" />
                  {selectedDate ? `Log ${selectedDate.toLocaleDateString()}` : "Select a date"}
                </button>
              ) : (
                <button
                  onClick={handleLogRange}
                  disabled={!selectedRange?.from || !selectedRange?.to || rangeSubmitting}
                  data-testid="log-presence-range-btn"
                  className="w-full bg-[#D4AF37] text-black py-2.5 text-sm font-medium hover:bg-[#B5952F] transition-colors disabled:opacity-30"
                >
                  <CalendarDays className="w-4 h-4 inline mr-2" />
                  {rangeSubmitting
                    ? "Working..."
                    : selectedRange?.from && selectedRange?.to
                      ? `Log ${Math.round((selectedRange.to - selectedRange.from) / 86400000) + 1} days`
                      : "Select a range"}
                </button>
              )}

              {logMode === "range" && selectedRange?.from && selectedRange?.to && (
                <button
                  onClick={handleDeleteRange}
                  disabled={rangeSubmitting}
                  data-testid="delete-presence-range-btn"
                  className="w-full bg-transparent border border-red-500/20 text-red-400/80 py-2 text-xs hover:border-red-500/40 hover:text-red-300 transition-colors disabled:opacity-30"
                >
                  <Trash2 className="w-3.5 h-3.5 inline mr-1.5" />
                  Delete logged days in range
                </button>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Styled confirm dialog (replaces native browser confirm) */}
      <ConfirmDialog
        open={!!confirmState}
        title={confirmState?.title}
        description={confirmState?.description}
        confirmLabel={confirmState?.confirmLabel}
        onConfirm={() => confirmState?.onConfirm?.()}
        onCancel={() => setConfirmState(null)}
        tone="danger"
      />

      {/* Add Residency Dialog */}
      <AnimatePresence>
        {showAddRes && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/70 z-50"
              onClick={() => setShowAddRes(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <div className="bg-[#0A0A0A] border border-white/[0.12] w-full max-w-lg" data-testid="add-residency-dialog">
                <div className="flex items-center justify-between p-6 border-b border-white/[0.10]">
                  <h2 className="font-serif text-xl text-white">Add Residency</h2>
                  <button onClick={() => setShowAddRes(false)} className="text-white/65 hover:text-white">
                    <span className="text-xl">&times;</span>
                  </button>
                </div>

                <form onSubmit={handleAddResidency} className="p-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] tracking-[0.15em] uppercase text-white/65 mb-1.5 block">Country</label>
                      <select
                        data-testid="res-country-select"
                        value={resForm.country}
                        onChange={e => {
                          const nextCountry = e.target.value;
                          const list = visaTypesFor(nextCountry);
                          setResForm(p => ({
                            ...p,
                            country: nextCountry,
                            // Auto-reset visa_type to the first valid option for the new country
                            // (skips picking "Other" as the default unless it's the only choice).
                            visa_type: list[0] || "Other",
                            visa_type_custom: "",
                          }));
                        }}
                        className="w-full bg-[#050505] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#D4AF37] outline-none"
                      >
                        {residencyCountries.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                      {resForm.country === "Other" && (
                        <input
                          type="text"
                          data-testid="res-country-custom"
                          value={resForm.country_custom || ""}
                          onChange={e => setResForm(p => ({ ...p, country_custom: e.target.value }))}
                          placeholder="e.g. Andorra, Vanuatu…"
                          className="mt-2 w-full bg-[#050505] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#D4AF37] outline-none"
                          required
                        />
                      )}
                    </div>
                    <div>
                      <label className="text-[10px] tracking-[0.15em] uppercase text-white/65 mb-1.5 block">Visa Type</label>
                      <select
                        data-testid="res-visa-select"
                        value={resForm.visa_type}
                        onChange={e => setResForm(p => ({ ...p, visa_type: e.target.value }))}
                        className="w-full bg-[#050505] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#D4AF37] outline-none"
                      >
                        {visaTypesFor(resForm.country).map(v => <option key={v} value={v}>{v}</option>)}
                      </select>
                      {resForm.visa_type === "Other" && (
                        <input
                          type="text"
                          data-testid="res-visa-custom"
                          value={resForm.visa_type_custom || ""}
                          onChange={e => setResForm(p => ({ ...p, visa_type_custom: e.target.value }))}
                          placeholder="Enter the exact visa / permit name"
                          className="mt-2 w-full bg-[#050505] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#D4AF37] outline-none"
                          required
                        />
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] tracking-[0.15em] uppercase text-white/65 mb-1.5 block">Start Date</label>
                      <input
                        data-testid="res-start-date"
                        type="date"
                        value={resForm.start_date}
                        onChange={e => setResForm(p => ({ ...p, start_date: e.target.value }))}
                        className="w-full bg-[#050505] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#D4AF37] outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-[10px] tracking-[0.15em] uppercase text-white/65 mb-1.5 block">Expiry Date</label>
                      <input
                        data-testid="res-expiry-date"
                        type="date"
                        value={resForm.expiry_date}
                        onChange={e => setResForm(p => ({ ...p, expiry_date: e.target.value }))}
                        className="w-full bg-[#050505] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#D4AF37] outline-none"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] tracking-[0.15em] uppercase text-white/65 mb-1.5 block">
                      Holder Name <span className="text-white/55 normal-case tracking-normal">· optional</span>
                    </label>
                    <input
                      data-testid="res-holder-input"
                      type="text"
                      value={resForm.holder_name}
                      onChange={e => setResForm(p => ({ ...p, holder_name: e.target.value }))}
                      className="w-full bg-[#050505] border border-white/10 text-white px-3 py-2.5 text-sm placeholder:text-white/55 focus:border-[#D4AF37] outline-none"
                      placeholder={user?.name || user?.email?.split("@")[0] || "Full name on the permit"}
                    />
                    <p className="text-white/60 text-[10px] mt-1.5">Leave blank to use your account name. Useful for spouses, children or company-issued permits.</p>
                  </div>

                  <div>
                    <label className="text-[10px] tracking-[0.15em] uppercase text-white/65 mb-1.5 block">Notes</label>
                    <textarea
                      data-testid="res-notes-input"
                      value={resForm.notes}
                      onChange={e => setResForm(p => ({ ...p, notes: e.target.value }))}
                      rows={2}
                      className="w-full bg-[#050505] border border-white/10 text-white px-3 py-2.5 text-sm placeholder:text-white/55 focus:border-[#D4AF37] outline-none resize-none"
                      placeholder="Additional notes..."
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="submit-residency-btn"
                    className="w-full bg-[#D4AF37] text-black py-3 text-sm font-medium hover:bg-[#B5952F] transition-colors"
                  >
                    Add Residency
                  </button>
                </form>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
